/**
 * Resolve which ticket records should drive section/row/seat display on order views.
 * Package orders use package_tickets (canonical assignment, updated on relocation),
 * matching packages report and invoice rendering.
 */

export function isPackageOrder(order) {
  return Boolean(order?.package || order?.packageOrder || order?.package_order)
}

export function getPackageTickets(order) {
  return order?.package_tickets || order?.packageTickets || []
}

function getPackageEventCount(order) {
  const events = order?.package?.events
  return Array.isArray(events) && events.length > 0 ? events.length : 0
}

/**
 * Per-game ticket count for season packages (seats × games), not package seat rows.
 */
function getPackagePerGameTicketCount(order) {
  const originalTickets = order?.originalTickets || order?.original_tickets
  if (originalTickets?.length > 0) return originalTickets.length

  const tickets = order?.tickets
  if (tickets?.length > 0) return tickets.length

  const packageTickets = getPackageTickets(order)
  const eventCount = getPackageEventCount(order)
  if (packageTickets.length > 0 && eventCount > 1) {
    return packageTickets.length * eventCount
  }

  return packageTickets.length || 0
}

/**
 * Tickets used for section/row/seat display.
 * Relocated season tickets read current locations from package_tickets.
 */
export function getOrderSeatLocationTickets(order) {
  if (!order) return []

  if (isPackageOrder(order)) {
    const packageTickets = getPackageTickets(order)
    if (packageTickets.length > 0) return packageTickets
    return (
      order.originalTickets || order.original_tickets || order.tickets || []
    )
  }

  const originalTickets = order.originalTickets || order.original_tickets
  if (originalTickets?.length > 0) return originalTickets
  return order.tickets || []
}

/**
 * Total ticket/voucher count shown on order summaries.
 * Package orders count per-game tickets; seat rows use getOrderSeatLocationTickets.
 */
export function getOrderTicketQuantity(order) {
  if (!order) return 0

  if (order.flex_pack && !order.voucher) {
    return order.flex_pack.gameTickets || 0
  }

  if (isPackageOrder(order)) {
    return getPackagePerGameTicketCount(order)
  }

  return getOrderSeatLocationTickets(order).length
}
